import React, { useState, useRef, useMemo, useEffect } from "react";

const generateTimeOptions = () => {
  const options = [];
  for (let totalMinutes = 0; totalMinutes < 24 * 60; totalMinutes++) {
    const h24 = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const period = h24 < 12 ? "AM" : "PM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    const label = `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
    options.push({ label, value: label });
  }
  return options;
};

const ALL_OPTIONS = generateTimeOptions();

const timeToMinutes = (timeStr) => {
  if (!timeStr) return -1;
  const parts = timeStr.trim().split(" ");
  if (parts.length < 2) return -1;
  const [time, period] = parts;
  const timeParts = time.split(":");
  if (timeParts.length < 2) return -1;
  let h = parseInt(timeParts[0], 10);
  const m = parseInt(timeParts[1], 10);
  if (isNaN(h) || isNaN(m)) return -1;
  if (period.toUpperCase() === "PM" && h !== 12) h += 12;
  if (period.toUpperCase() === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

const isFormattedTime = (s) =>
  /^(0[1-9]|1[0-2]):[0-5][0-9]\s(AM|PM)$/i.test(s?.trim());

const smartPeriod = (inputHour) => {
  const now = new Date();
  const currentMins = now.getHours() * 60 + now.getMinutes();
  const amHour = inputHour === 12 ? 0 : inputHour;
  const pmHour = inputHour === 12 ? 12 : inputHour + 12;
  const amMins = amHour * 60;
  const pmMins = pmHour * 60;
  const amDiff =
    amMins >= currentMins
      ? amMins - currentMins
      : 24 * 60 + amMins - currentMins;
  const pmDiff =
    pmMins >= currentMins
      ? pmMins - currentMins
      : 24 * 60 + pmMins - currentMins;
  return amDiff <= pmDiff ? "AM" : "PM";
};

const parseRawInput = (raw) => {
  if (!raw) return null;
  const trimmed = raw.trim();

  if (isFormattedTime(trimmed)) return trimmed.toUpperCase();

  const match = trimmed.match(/^(\d{1,4})(?::(\d{2}))?\s*(a|am|p|pm)?$/i);
  if (!match) return null;

  const hourDigits = match[1];
  const minuteDigits = match[2];
  const suffix = (match[3] || "").toLowerCase();
  const hasPeriod = suffix.startsWith("p") || suffix.startsWith("a");
  const forcedPeriod = suffix.startsWith("p") ? "PM" : "AM";

  let h, m;

  if (minuteDigits !== undefined) {
    h = parseInt(hourDigits, 10);
    m = parseInt(minuteDigits, 10);
  } else if (hourDigits.length <= 2) {
    h = parseInt(hourDigits, 10);
    m = 0;
  } else if (hourDigits.length === 3) {
    h = parseInt(hourDigits[0], 10);
    m = parseInt(hourDigits.slice(1), 10);
  } else {
    h = parseInt(hourDigits.slice(0, 2), 10);
    m = parseInt(hourDigits.slice(2), 10);
  }

  if (isNaN(h) || isNaN(m) || m > 59) return null;

  if (h > 12 && h <= 23) {
    return `${String(h - 12).padStart(2, "0")}:${String(m).padStart(2, "0")} PM`;
  }
  if (h === 0) h = 12;
  if (h > 23) return null;

  const finalPeriod = hasPeriod ? forcedPeriod : smartPeriod(h);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${finalPeriod}`;
};

const TimeInput = ({
  value,
  onSelect,
  placeholder,
  active,
  onFocus,
  minTime,
  parentFocused,
}) => {
  const [inputVal, setInputVal] = useState(value || "");
  const [dropOpen, setDropOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setInputVal(value || "");
  }, [value]);

  useEffect(() => {
    if (active) {
      setTimeout(() => {
        inputRef.current?.focus();
        setDropOpen(true);
      }, 50);
    }
  }, [active]);

  useEffect(() => {
    if (dropOpen && listRef.current) {
      const highlighted = listRef.current.querySelector(
        "[data-highlighted='true']",
      );
      if (highlighted) highlighted.scrollIntoView({ block: "nearest" });
    }
  }, [dropOpen, inputVal]);

  const { filtered, highlightedValue } = useMemo(() => {
    let opts = ALL_OPTIONS;
    if (minTime) {
      const minMins = timeToMinutes(minTime);
      opts = opts.filter((o) => timeToMinutes(o.value) > minMins);
    }

    const q = inputVal.trim();
    if (!q) return { filtered: opts, highlightedValue: null };

    const parsed = parseRawInput(q);

    const digits = q.replace(/[^0-9]/g, "");
    let result = opts;

    if (digits.length > 0) {
      result = opts.filter((o) => {
        const labelDigits = o.label.replace(/[^0-9]/g, "");
        return labelDigits.startsWith(digits);
      });
      const suffix = q.replace(/[^apm]/gi, "").toLowerCase();
      if (suffix.startsWith("p")) {
        result = result.filter((o) => o.label.includes("PM"));
      } else if (suffix.startsWith("a")) {
        result = result.filter((o) => o.label.includes("AM"));
      }
    }

    return {
      filtered: result.length > 0 ? result : opts,
      highlightedValue: parsed,
    };
  }, [inputVal, minTime]);

  const commitInput = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    if (isFormattedTime(trimmed)) {
      onSelect(trimmed.toUpperCase());
      return;
    }
    const parsed = parseRawInput(trimmed);
    if (parsed) {
      setInputVal(parsed);
      onSelect(parsed);
    }
  };

  const handleChange = (e) => {
    setInputVal(e.target.value);
    setDropOpen(true);
  };

  const handleSelect = (opt) => {
    setInputVal(opt.label);
    onSelect(opt.value);
    setDropOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      commitInput();
      setDropOpen(false);
      if (e.key === "Enter") e.preventDefault();
    }
  };

  const handleBlur = (_e) => {
    setTimeout(() => {
      setDropOpen(false);
      commitInput();
    }, 150);
  };

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        ref={inputRef}
        value={inputVal}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          setDropOpen(true);
          onFocus && onFocus();
        }}
        onBlur={(e) => {
          setIsFocused(false);
          handleBlur(e);
        }}
        // placeholder={isFocused ? placeholder : ""}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          width: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: 14,
          color: inputVal ? "#000" : "#bfbfbf",
          cursor: "text",
          padding: 0,
        }}
      />
      {dropOpen && filtered.length > 0 && (
        <ul
          ref={listRef}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 1050,
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            maxHeight: 200,
            overflowY: "auto",
            listStyle: "none",
            margin: 0,
            padding: "4px 0",
            minWidth: 140,
          }}
        >
          {filtered.map((opt, i) => {
            const isHighlighted = opt.value === highlightedValue;
            return (
              <li
                key={i}
                data-highlighted={isHighlighted ? "true" : "false"}
                onMouseDown={() => handleSelect(opt)}
                style={{
                  padding: "5px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  color: "#333",
                  background: isHighlighted ? "#e6f4ff" : "transparent",
                  fontWeight: isHighlighted ? 500 : 400,
                }}
                onMouseEnter={(e) => {
                  if (!isHighlighted)
                    e.currentTarget.style.background = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isHighlighted
                    ? "#e6f4ff"
                    : "transparent";
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const CustomTimeRangePicker = ({
  value,
  onChange,
  placeholder = ["Start Time", "End Time"],
  disabled = false,
  label,
  required = false,
  error,
}) => {
  const [focused, setFocused] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  const startVal = value?.[0] || "";
  const endVal = value?.[1] || "";
  // const isOccupied = focused || !!startVal || !!endVal;
  const isOccupied = true;

  const handleStartSelect = (selected) => {
    if (endVal && timeToMinutes(endVal) <= timeToMinutes(selected)) {
      onChange([selected, ""]);
    } else {
      onChange([selected, endVal]);
    }
    setTimeout(() => setActivePanel("end"), 200);
  };

  const handleEndSelect = (selected) => {
    onChange([startVal, selected]);
    setActivePanel(null);
    setFocused(false);
  };

  const clockIcon = (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#bfbfbf"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );

  const arrowIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#bfbfbf"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, margin: "0 6px" }}
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        onFocus={() => {
          if (!disabled) setFocused(true);
        }}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        style={{
          position: "relative",
          border: `1px solid ${error ? "#ff4d4f" : focused ? "#1677ff" : "#000000"}`,
          padding: "5px 6px",
          background: disabled ? "#f5f5f5" : "#fff",
          display: "flex",
          alignItems: "center",
          cursor: disabled ? "not-allowed" : "text",
          transition: "border-color 0.2s, box-shadow 0.2s",
          boxShadow: focused ? "0 0 0 2px rgba(22,119,255,0.2)" : "none",
        }}
      >
        <label
          style={{
            position: "absolute",
            top: -8,
            left: 8,
            transform: "none",
            fontSize: 11,
            color: error ? "#ff4d4f" : focused ? "#1677ff" : "rgba(0,0,0,0.45)",
            background: "#fff",
            padding: "0 4px",
            pointerEvents: "none",
            lineHeight: "14px",
            whiteSpace: "nowrap",
            zIndex: 2,
          }}
        >
          {label}
          {/* {required && (
            <span style={{ color: "#ff4d4f", marginLeft: 2 }}>*</span>
          )} */}
        </label>

        <TimeInput
          value={startVal}
          onSelect={handleStartSelect}
          placeholder={placeholder[0]}
          active={activePanel === "start"}
          onFocus={() => setActivePanel("start")}
        />

        {arrowIcon}

        <TimeInput
          value={endVal}
          onSelect={handleEndSelect}
          placeholder={placeholder[1]}
          active={activePanel === "end"}
          onFocus={() => setActivePanel("end")}
          minTime={startVal}
        />

        {clockIcon}
      </div>

      {error && (
        <div
          style={{
            color: "#ff4d4f",
            fontSize: 12,
            marginTop: 4,
            paddingLeft: 2,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default CustomTimeRangePicker;
